import { useMemo, useState } from 'react';
import { Plus, Pencil, MoreHorizontal } from 'lucide-react';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Chip, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, IconButton } from '@vnetwork/ui/primitives';
import { Section } from '@vnetwork/ui/layout';
import { formatDate } from '@/lib/format';
import { useCustomerMailers, type CustomerMailerRow } from '@/api/customer-mailers';
import { TabError, TabLoading } from '../tab-states';
import { MailerFormDialog } from './mailer-form-dialog';

/** Split a comma/newline-separated recipient string into trimmed addresses. */
function splitRecipients(value?: string | null): string[] {
  if (!value) return [];
  return value
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Recipient chips (receiver, plus cc/bcc-labelled) with a +N overflow chip. */
function RecipientChips({ row }: { row: CustomerMailerRow }) {
  const to = splitRecipients(row.mailer_receiver);
  const cc = splitRecipients(row.mailer_cc);
  const bcc = splitRecipients(row.mailer_bcc);
  if (to.length + cc.length + bcc.length === 0) return <>—</>;

  const shown = to.slice(0, 3);
  const overflow = to.length - shown.length;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
      {shown.map((email) => (
        <Chip key={email} size="sm" appearance="outline">
          {email}
        </Chip>
      ))}
      {overflow > 0 && (
        <Chip size="sm" appearance="subtle" tone="neutral">
          +{overflow}
        </Chip>
      )}
      {cc.length > 0 && (
        <Chip size="sm" appearance="subtle" tone="info">
          CC {cc.length}
        </Chip>
      )}
      {bcc.length > 0 && (
        <Chip size="sm" appearance="subtle" tone="info">
          BCC {bcc.length}
        </Chip>
      )}
    </div>
  );
}

/** Purpose chips from `mailer_types[]` (name), with a +N overflow chip. */
function PurposeChips({ row }: { row: CustomerMailerRow }) {
  const names = (row.mailer_types ?? [])
    .map((t) => t.mailer_type_name)
    .filter((n): n is string => Boolean(n));
  if (names.length === 0) return <>—</>;
  const shown = names.slice(0, 2);
  const overflow = names.length - shown.length;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
      {shown.map((name) => (
        <Chip key={name} size="sm" appearance="subtle">
          {name}
        </Chip>
      ))}
      {overflow > 0 && (
        <Chip size="sm" appearance="subtle" tone="neutral">
          +{overflow}
        </Chip>
      )}
    </div>
  );
}

function useMailerColumns(onEdit: (row: CustomerMailerRow) => void) {
  return useMemo<ColumnDef<CustomerMailerRow, unknown>[]>(
    () => [
      {
        id: 'customer_mailer_name',
        header: 'Mailer',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)', overflowWrap: 'anywhere' }}>
            {row.original.customer_mailer_name || '—'}
          </span>
        ),
      },
      {
        id: 'purposes',
        header: 'Purpose',
        cell: ({ row }) => <PurposeChips row={row.original} />,
      },
      {
        id: 'recipients',
        header: 'Recipients',
        cell: ({ row }) => <RecipientChips row={row.original} />,
      },
      {
        id: 'sendable',
        header: 'Sendable',
        cell: ({ row }) => (
          <StatusPill tone={row.original.sendable ? 'success' : 'danger'}>
            {row.original.sendable ? 'Yes' : 'No'}
          </StatusPill>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        cell: ({ row }) => formatDate(row.original.created_at),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Row actions">
                <MoreHorizontal size={15} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onEdit(row.original)}>
                <Pencil size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [onEdit],
  );
}

/**
 * Configured mailers — the customer's mailer definitions (flat, no pager).
 * Add the Create Mailer button and per-row Edit action via MailerFormDialog.
 * USER submits live create/edit mutations.
 */
export function ConfiguredMailersSection({ customerId }: { customerId: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editMailer, setEditMailer] = useState<CustomerMailerRow | undefined>();

  const columns = useMailerColumns((row) => setEditMailer(row));
  const { data, isLoading, isError, refetch } = useCustomerMailers(customerId);
  const rows = data?.results ?? [];

  return (
    <Section title="Configured Mailers">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--vn-space-3)' }}>
        <Button variant="secondary" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
          Add Mailer
        </Button>
      </div>

      {isError ? (
        <TabError title="Failed to load configured mailers" onRetry={() => refetch()} />
      ) : isLoading ? (
        <TabLoading rows={4} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          enableSorting={false}
          getRowId={(r) => String(r.customer_mailer_id)}
          emptyState={
            <EmptyState
              title="No configured mailers"
              description="This customer has no mailers set up yet."
            />
          }
        />
      )}

      {/* Create dialog */}
      <MailerFormDialog
        customerId={customerId}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      {/* Edit dialog — mounted only when a mailer is selected */}
      {editMailer ? (
        <MailerFormDialog
          customerId={customerId}
          mailer={editMailer}
          open={!!editMailer}
          onOpenChange={(o) => { if (!o) setEditMailer(undefined); }}
        />
      ) : null}
    </Section>
  );
}
